import { SingleSpeakerSelect } from '@src/components/content-components/SingleSpeakerSelect';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getUnifiedValue } from '@src/utility/compositionSettings';
import { LogManager } from '@src/utility/Logger';
import { speakerNameLine } from '@src/utility/speakerRole';
import {
  buildSpeakersFromMappings,
  extractSpeakerLabelsFromTranscriptElements,
  fetchDatoSpeakers,
  fetchSpeakerVoiceMappings,
} from '@src/utility/speakerUtils';
import { useEffect, useState } from 'react';

import { ControlFieldLabel } from './ControlField';

import type { ControlRenderProps, SpeakerControlConfiguration } from '../types';
import type { Speaker } from '@src/components/content-components/SpeakerTypes';

export const SpeakerControl = ({
  control,
  activeElement,
  onSpeakerControlChange,
  compositionMeta,
}: ControlRenderProps<SpeakerControlConfiguration>) => {
  const logger = LogManager.getLogger('component:SpeakerControl');
  const videoCreator = useVideoCreatorStore();

  const { unifiedValue } = getUnifiedValue(
    control,
    activeElement,
    compositionMeta,
  );
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker | null>(null);
  const [isLoadingSpeakers, setIsLoadingSpeakers] = useState(false);

  useEffect(() => {
    const storyId = videoCreator.story?.id;
    if (!storyId) return;
    let cancelled = false;
    const loadSpeakers = async () => {
      setIsLoadingSpeakers(true);
      try {
        const transcriptElements = videoCreator.rootStore?.transcriptionStore
          ?.getFinalTranscriptionElements?.()
          ?.filter(el => el.state !== 'removed' && el.state !== 'cut');
        const labels =
          extractSpeakerLabelsFromTranscriptElements(transcriptElements);
        const [speakerMappings, datoSpeakers] = await Promise.all([
          fetchSpeakerVoiceMappings(storyId),
          fetchDatoSpeakers(storyId, videoCreator.datoClientStore),
        ]);
        if (cancelled) return;

        // Build speakers from transcript labels (if any)
        const built = labels.length
          ? buildSpeakersFromMappings(labels, speakerMappings, datoSpeakers)
          : [];

        // Merge in all Story speakers not already matched from transcript
        const builtIds = new Set(
          built.map(s => s.id).filter(Boolean) as string[],
        );
        const additionalSpeakers: Speaker[] = datoSpeakers
          // A speaker with only First / Last Name still has a Name line (PLAT-12176).
          .filter(ds => speakerNameLine(ds) && !builtIds.has(ds.id))
          .map(ds => ({
            id: ds.id,
            speakerLabel: `override_${ds.id}`,
            name: speakerNameLine(ds),
            firstName: ds.firstName,
            lastName: ds.lastName,
            title: ds.title,
            role: ds.role,
            organization: ds.organization,
            headshots: ds.headshots?.map(h => h.url) ?? [],
            isAnonymous: false,
          }));

        setSpeakers([...built, ...additionalSpeakers]);
      } catch (error) {
        logger.warn('Failed to load speakers', { error });
      } finally {
        if (!cancelled) setIsLoadingSpeakers(false);
      }
    };
    loadSpeakers();

    return () => {
      cancelled = true;
    };
  }, [
    videoCreator.story?.id,
    videoCreator.datoClientStore,
    videoCreator.rootStore,
  ]);

  useEffect(() => {
    // If unifiedValue changes from outside, update selectedSpeaker accordingly
    if (typeof unifiedValue === 'string') {
      const match = speakers.find(s => s.speakerLabel === unifiedValue);
      setSelectedSpeaker(match || null);
    } else {
      setSelectedSpeaker(null);
    }
  }, [unifiedValue, speakers]);

  const handleSpeakerChange = (next: Speaker[]) => {
    const first = next[0] ?? null;
    if (
      first &&
      (!selectedSpeaker ||
        selectedSpeaker.speakerLabel !== first.speakerLabel) &&
      typeof onSpeakerControlChange === 'function' &&
      typeof control.controlBindings !== 'undefined'
    ) {
      setSelectedSpeaker(first);
      onSpeakerControlChange(control.controlBindings, first);
    }
  };
  if (isLoadingSpeakers || speakers.length === 0) {
    return null;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <ControlFieldLabel>{control.label}</ControlFieldLabel>
      <SingleSpeakerSelect
        speakers={speakers}
        selectedSpeakers={selectedSpeaker ? [selectedSpeaker] : []}
        onChange={handleSpeakerChange}
      />
    </div>
  );
};
